// Settings (the Mac's Settings window, as much of it as belongs to a browser): Appearance (Match System, Paper or
// Lamplight; Contrast; Steady lines in mind maps) and Writing (Font, Show formatting marks). Each is kept in this browser (localStorage) and takes effect
// at once in every open view; the account itself — passkeys, devices, signing out — is the profile's.
import { useEffect, useRef } from 'react';
import { setAppearance, useAppearance, type Appearance } from '../shell/appearance.ts';
import { setContrast, useContrast, type Contrast } from '../shell/contrast.ts';
import { FACES, setFace, useFace } from '../shell/face.ts';
import { Masthead } from '../shell/Gate.tsx';
import { setMapSteadyLines, setShowsFormattingMarks, useMapSteadyLines, useShowsFormattingMarks } from '../shell/preferences.ts';
import { followLink, pageBeforeSettings } from '../shell/route.ts';

const APPEARANCES: { value: Appearance; label: string; note: string }[] = [
  { value: 'system', label: 'Match System', note: 'Paper when your computer is light, Lamplight when it’s dark.' },
  { value: 'paper', label: 'Paper', note: 'Dark ink on warm paper, always.' },
  { value: 'lamplight', label: 'Lamplight', note: 'Pale ink on a dark page, always.' },
];

const CONTRASTS: { value: Contrast; label: string; note: string }[] = [
  { value: 'normal', label: 'Normal', note: 'The appearance’s own colours.' },
  { value: 'more', label: 'High contrast', note: 'Black ink on white paper, or white on black, with stronger rules.' },
  { value: 'yellow', label: 'Yellow on black', note: 'Yellow ink on a black page, whatever the colour scheme above.' },
];

export function SettingsPage() {
  const heading = useRef<HTMLHeadingElement>(null);
  const appearance = useAppearance();
  const contrast = useContrast();
  const face = useFace();
  const marks = useShowsFormattingMarks();
  const steady = useMapSteadyLines();
  useEffect(() => {
    document.title = 'Settings — Five Rooms';
    heading.current?.focus();
  }, []);
  const back = pageBeforeSettings();
  return (
    <div className="calm-page">
      <Masthead signedIn />
      <main className="calm settings-page" data-state="settings">
        <p className="settings-back">
          <a href={back.href} onClick={(e) => followLink(e, back.href)}>
            ← {back.label}
          </a>
        </p>
        <h1 ref={heading} tabIndex={-1}>
          Settings
        </h1>
        <p className="lede">These are kept in this browser. Your account, passkeys and signing out are on your profile.</p>

        <section className="settings-section" aria-labelledby="settings-appearance">
          <h2 id="settings-appearance" className="eyebrow">
            Appearance
          </h2>
          <fieldset className="settings-choices" data-appearance-choice={appearance}>
            <legend>Colour scheme</legend>
            {APPEARANCES.map((a) => (
              <label key={a.value} className="settings-choice">
                <input type="radio" name="appearance" value={a.value} checked={appearance === a.value} onChange={() => setAppearance(a.value)} />
                <span className="settings-choice-text">
                  <span className="settings-choice-label">{a.label}</span>
                  <span className="settings-note">{a.note}</span>
                </span>
              </label>
            ))}
          </fieldset>
          <fieldset className="settings-choices settings-group" data-contrast-choice={contrast}>
            <legend>Contrast</legend>
            {CONTRASTS.map((c) => (
              <label key={c.value} className="settings-choice">
                <input type="radio" name="contrast" value={c.value} checked={contrast === c.value} onChange={() => setContrast(c.value)} />
                <span className="settings-choice-text">
                  <span className="settings-choice-label">{c.label}</span>
                  <span className="settings-note">{c.note}</span>
                </span>
              </label>
            ))}
            <p className="settings-note settings-foot">Your computer’s Increase Contrast setting turns high contrast on by itself.</p>
          </fieldset>
          <label className="settings-choice settings-toggle settings-group">
            <input type="checkbox" checked={steady} onChange={(e) => setMapSteadyLines(e.target.checked)} data-map-steady-lines />
            <span className="settings-choice-text">
              <span className="settings-choice-label">Steady lines in mind maps</span>
              <span className="settings-note">The maps’ charcoal lines and pencil outlines drawn without their wobble.</span>
            </span>
          </label>
        </section>

        <section className="settings-section" aria-labelledby="settings-writing">
          <h2 id="settings-writing" className="eyebrow">
            Writing
          </h2>
          <fieldset className="settings-choices" data-face-choice={face}>
            <legend>Font</legend>
            {FACES.map((f) => (
              <label key={f.value} className="settings-choice">
                <input type="radio" name="face" value={f.value} checked={face === f.value} onChange={() => setFace(f.value)} />
                <span className="settings-choice-text">
                  <span className={`settings-choice-label face-${f.value}`}>{f.label}</span>
                  <span className="settings-note">{f.note}</span>
                </span>
              </label>
            ))}
            <p className={`settings-face-line face-${face}`} data-face-sample>
              It was on the Sunday evening, as Utterson walked home, that the door opened.
            </p>
            <p className="settings-note settings-foot">The page in Write and Research. Scripts stay in Courier Prime, and what you publish is set by the format.</p>
          </fieldset>
          <label className="settings-choice settings-toggle settings-group">
            <input type="checkbox" checked={marks} onChange={(e) => setShowsFormattingMarks(e.target.checked)} data-formatting-marks />
            <span className="settings-choice-text">
              <span className="settings-choice-label">Show formatting marks</span>
              <span className="settings-note">
                The asterisks around italic and bold, and the brackets around small capitals, underlining and colour, drawn faintly in the text instead of
                hidden. In a script, Fountain’s marks always stay in view.
              </span>
            </span>
          </label>
        </section>

        <p className="library-foot">
          <a href="/profile" onClick={(e) => followLink(e, '/profile')}>
            Your profile
          </a>
          <a href="/help" onClick={(e) => followLink(e, '/help')}>
            Help
          </a>
        </p>
      </main>
    </div>
  );
}
